// Dashboard module for Docospot
// Handles the main dashboard view with stats, account hierarchy, and alarms

import { t } from '../core/i18n.js';
import { auth } from '../core/auth.js';
import { apiCall } from '../core/api.js';
import { API_BASE_URL } from '../core/config.js';
import { showError } from '../core/ui.js';
import { getCurrentUser } from '../utils/helpers.js';
import { updatePageTitle, updateActiveNavigation, showLoginScreen } from './navigation.js';

/**
 * Display the dashboard page
 */
export function showDashboard() {
    console.log('showDashboard() called');
    const mainContent = document.getElementById('main-content');
    if (!mainContent) {
        console.log('main-content element not found');
        return;
    }

    // Check if user is logged in
    const currentUser = getCurrentUser();
    console.log('showDashboard - currentUser:', currentUser);
    console.log('showDashboard - account_name:', currentUser?.account_name);
    const isLoggedIn = auth.isAuthenticated();
    console.log('isLoggedIn:', isLoggedIn);

    if (!isLoggedIn) {
        // Show the proper login screen with OAuth buttons and username/password form
        showLoginScreen();
        return;
    }

    // User is logged in - show sidebar and header
    const sidebar = document.getElementById('sidebar');
    const header = document.querySelector('header');
    if (sidebar) sidebar.style.display = 'block';
    if (header) header.style.display = 'block';
    
    // Restore main content margins
    mainContent.style.marginLeft = '';
    mainContent.style.paddingTop = '';

    // User is logged in - show full dashboard
    mainContent.innerHTML = `
        <div class="mb-8">
            <h1 class="text-2xl font-semibold text-gray-900">${t('dashboard.title')}</h1>
            <p class="mt-2 text-sm text-gray-700">${t('dashboard.welcomeBack')}, ${currentUser ? currentUser.full_name : 'User'}!</p>
            ${currentUser ? `
                <div class="mt-4 p-4 bg-blue-50 rounded-lg">
                    <div class="flex items-center justify-between">
                        <div>
                            <h3 class="text-sm font-medium text-blue-800">${t('dashboard.accountInfo')}</h3>
                            <p class="text-sm text-blue-600">
                                <span class="font-medium">${currentUser.role}</span>${currentUser.account_name ? ` ${t('dashboard.at')} <span class="font-medium">${currentUser.account_name}</span>` : ''}
                            </p>
                        </div>
                        <button onclick="logout()" class="text-sm text-blue-600 hover:text-blue-800">${t('nav.logout')}</button>
                    </div>
                </div>
            ` : ''}
        </div>
        
        <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <!-- Stats cards -->
            <div class="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
                <dt class="truncate text-sm font-medium text-gray-500">${t('dashboard.totalAccounts')}</dt>
                <dd class="mt-1 text-3xl font-semibold tracking-tight text-gray-900" id="total-accounts">-</dd>
            </div>
            
            <div class="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
                <dt class="truncate text-sm font-medium text-gray-500">${t('dashboard.totalUsers')}</dt>
                <dd class="mt-1 text-3xl font-semibold tracking-tight text-gray-900" id="total-users">-</dd>
            </div>
            
            <div class="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
                <dt class="truncate text-sm font-medium text-gray-500">${t('dashboard.totalLocations')}</dt>
                <dd class="mt-1 text-3xl font-semibold tracking-tight text-gray-900" id="total-locations">-</dd>
            </div>
        </div>

        <div class="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
                <h2 class="text-lg font-medium text-gray-900 mb-4">${t('dashboard.accountHierarchy')}</h2>
                <div id="account-hierarchy" class="bg-white shadow rounded-lg p-6">
                    <div class="text-center text-gray-500">${t('dashboard.loadingAccounts')}</div>
                </div>
            </div>
            
            <div>
                <h2 class="text-lg font-medium text-gray-900 mb-4">${t('dashboard.alarms')}</h2>
                <div id="alarms-list" class="bg-white shadow rounded-lg p-6">
                    <div class="text-center text-gray-500">${t('dashboard.loadingAlarms')}</div>
                </div>
            </div>
        </div>
    `;

    // Load dashboard data
    updatePageTitle(t('dashboard.title'));
    loadDashboardData();
    updateActiveNavigation('dashboard');
}

/**
 * Load all dashboard data (stats, hierarchy, alarms)
 */
export async function loadDashboardData() {
    console.log('🚀 loadDashboardData called');
    console.log('🔑 auth.token:', auth.token);
    console.log('🔑 auth.isAuthenticated():', auth.isAuthenticated());
    
    if (!auth.isAuthenticated()) {
        console.log('❌ Not authenticated in loadDashboardData, returning early');
        return; // Don't load data if not authenticated
    }
    
    console.log('✅ Authenticated in loadDashboardData, proceeding...');

    try {
        // Load dashboard statistics
        const statsResponse = await fetch(`${API_BASE_URL}/dashboard/stats`, {
            headers: auth.getAuthHeaders()
        });
        
        if (statsResponse.ok) {
            const stats = await statsResponse.json();
            
            // Update statistics
            const totalAccountsEl = document.getElementById('total-accounts');
            const totalUsersEl = document.getElementById('total-users');
            const totalLocationsEl = document.getElementById('total-locations');
            
            if (totalAccountsEl) totalAccountsEl.textContent = stats.total_accounts || '0';
            if (totalUsersEl) totalUsersEl.textContent = stats.total_users || '0';
            if (totalLocationsEl) totalLocationsEl.textContent = stats.total_locations || '0';
        }
        
        // Load account hierarchy
        await loadAccountHierarchy();
        
        // Load alarms
        await loadDashboardAlarms();
        
    } catch (error) {
        console.error('Failed to load dashboard data:', error);
        showError('Failed to load dashboard data');
    }
}

/**
 * Load and display account hierarchy tree
 */
export async function loadAccountHierarchy() {
    console.log('🔑 Checking authentication for hierarchy load...');
    if (!auth.isAuthenticated()) {
        console.log('❌ Not authenticated, skipping account hierarchy load');
        return;
    }
    console.log('✅ Authenticated, loading account hierarchy...');
    try {
        const data = await apiCall('/accounts/hierarchy', {
            method: 'GET',
            headers: auth.getAuthHeaders()
        });
        const hierarchyContainer = document.getElementById('account-hierarchy');
        if (hierarchyContainer && data && data.accounts_tree) {
            hierarchyContainer.innerHTML = renderAccountTree(data.accounts_tree);
        } else if (hierarchyContainer) {
            hierarchyContainer.innerHTML = '<div class="text-center text-gray-500">No accounts found</div>';
        }
    } catch (error) {
        console.error('Failed to load account hierarchy:', error);
        const hierarchyContainer = document.getElementById('account-hierarchy');
        if (hierarchyContainer) {
            hierarchyContainer.innerHTML = '<div class="text-center text-red-500">Failed to load account hierarchy</div>';
        }
    }
}

/**
 * Recursively render account tree with expandable items
 * @param {Array} accounts - Array of account objects
 * @param {number} level - Current nesting level
 * @returns {string} HTML string
 */
export function renderAccountTree(accounts, level = 0) {
    let html = '<ul class="space-y-1">';
    
    accounts.forEach(account => {
        const hasChildren = account.children && account.children.length > 0;
        const accountId = account._id || account.id;
        const directUsers = account.direct_users || 0;
        const totalUsers = account.current_users || 0;
        
        html += `
            <li class="account-item" data-level="${level}">
                <div class="flex items-center py-2 px-2 hover:bg-gray-50 rounded">
                    ${hasChildren ? `
                        <button 
                            onclick="toggleDashboardAccountItem('${accountId}')" 
                            class="mr-2 text-gray-400 hover:text-gray-600 focus:outline-none transition-transform duration-200"
                            id="toggle-${accountId}"
                        >
                            <svg class="w-4 h-4 transform" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"></path>
                            </svg>
                        </button>
                    ` : `
                        <span class="w-6 mr-2"></span>
                    `}
                    <div class="flex-1">
                        <div class="flex items-center">
                            <span class="font-medium text-gray-900">${account.account_name}</span>
                        </div>
                        <div class="text-xs text-gray-500 mt-0.5">
                            ${directUsers} direct, ${totalUsers} total users
                        </div>
                    </div>
                </div>
                ${hasChildren ? `
                    <div class="children ml-6 hidden" id="children-${accountId}">
                        ${renderAccountTree(account.children, level + 1)}
                    </div>
                ` : ''}
            </li>
        `;
    });
    
    html += '</ul>';
    return html;
}

/**
 * Toggle account item expansion in dashboard
 * @param {string} accountId - Account ID to toggle
 */
export function toggleDashboardAccountItem(accountId) {
    const childrenDiv = document.getElementById(`children-${accountId}`);
    const toggleBtn = document.getElementById(`toggle-${accountId}`);
    
    if (childrenDiv && toggleBtn) {
        const isHidden = childrenDiv.classList.contains('hidden');
        
        if (isHidden) {
            childrenDiv.classList.remove('hidden');
            toggleBtn.querySelector('svg').style.transform = 'rotate(90deg)';
        } else {
            childrenDiv.classList.add('hidden');
            toggleBtn.querySelector('svg').style.transform = 'rotate(0deg)';
        }
    }
}

/**
 * Load and display dashboard alarms
 */
export async function loadDashboardAlarms() {
    if (!auth.isAuthenticated()) {
        console.log('Not authenticated, skipping alarms load');
        return;
    }
    
    try {
        const alarmsContainer = document.getElementById('alarms-list');
        if (!alarmsContainer) return;
        
        // For now, show a placeholder message
        alarmsContainer.innerHTML = `
            <div class="text-center text-gray-500 py-8">
                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                <p class="mt-2 text-sm">No active alarms</p>
            </div>
        `;
    } catch (error) {
        console.error('Failed to load alarms:', error);
        const alarmsContainer = document.getElementById('alarms-list');
        if (alarmsContainer) {
            alarmsContainer.innerHTML = '<div class="text-center text-red-500">Failed to load alarms</div>';
        }
    }
}

export default {
    showDashboard,
    loadDashboardData,
    loadAccountHierarchy,
    renderAccountTree,
    toggleDashboardAccountItem,
    loadDashboardAlarms
};
